import { sum } from 'lib/array/arrayUtils'
import { isFlashHotelType } from 'lib/offer/offerTypes'

export function getTotalChildren(filters?: App.OfferListFilters): number {
  return filters?.rooms?.reduce((sum, room) => sum + (room.children ?? 0), 0) ?? 0
}

export function getTotalInfants(filters?: App.OfferListFilters): number {
  return filters?.rooms?.reduce((sum, room) => sum + (room.infants ?? 0), 0) ?? 0
}

/**
 * Returns the maximum number of children and infants that are genuinely free
 * for a single room rate by checking entries at the maximum adult capacity.
 *
 * When the max adult capacity entry also includes children or infants, those
 * guests are extra — truly free on top of the room's full adult occupancy.
 * When children only appear at lower adult counts they are filling adult slots,
 * not staying for free.
 */
export function getFreeGuestTypesAtMaxOccupancy(
  capacities: Array<App.RoomCapacity>,
): { maxFreeChildren: number; maxFreeInfants: number } {
  if (capacities.length === 0) return { maxFreeChildren: 0, maxFreeInfants: 0 }

  const maxAdults = Math.max(...capacities.map(c => c.adults))
  const atMaxOccupancy = capacities.filter(c => c.adults === maxAdults)

  return {
    maxFreeChildren: Math.max(...atMaxOccupancy.map(c => c.children)),
    maxFreeInfants: Math.max(...atMaxOccupancy.map(c => c.infants)),
  }
}

/**
 * Returns the maximum number of children and infants that are included in the
 * base rate (no surcharge) for a single room rate.
 *
 * Uses the same max-adult-level algorithm as `getFreeGuestTypesAtMaxOccupancy`:
 * finds the maximum adult count across all `includedGuests` entries, then
 * returns the highest children/infants counts at that adult level.
 *
 * When `includedGuests` is empty, returns `undefined` so callers can fall back
 * to capacity-only logic.
 */
export function getIncludedGuestTypesAtMaxAdults(
  includedGuests: Array<App.PackageRoomIncludedGuests>,
): { maxIncludedChildren: number; maxIncludedInfants: number } | undefined {
  if (includedGuests.length === 0) return undefined

  const maxAdults = Math.max(...includedGuests.map(ig => ig.adults))
  const atMaxAdults = includedGuests.filter(ig => ig.adults === maxAdults)

  return {
    maxIncludedChildren: Math.max(...atMaxAdults.map(ig => ig.children)),
    maxIncludedInfants: Math.max(...atMaxAdults.map(ig => ig.infants)),
  }
}

/**
 * Returns the number of children/infants that stay free based on room rate
 * capacities and included guests across all packages.
 *
 * Each inner array represents one room rate's `capacities` / `includedGuests`.
 * We check each rate independently:
 *
 * 1. **When `includedGuests` data exists** — it is the source of truth for
 *    pricing. The free count is the number of children/infants included in
 *    the base rate, capped by the maximum the room can physically hold
 *    (across any capacity entry). Capacity and included guests are not
 *    directly correlated — a room may fit 10 adults but only include 2 in
 *    the base rate.
 *
 * 2. **When no `includedGuests` data exists** — falls back to the capacity
 *    heuristic: find max adult capacity, then count children/infants at that
 *    level. This avoids false positives for large-capacity rooms where
 *    children simply fill adult slots.
 *
 * The free count is capped per search room by the best rate so that partial
 * coverage (e.g. room includes 1 free child but user searched for 3) is
 * reflected accurately.
 *
 * Returns 0 when the offer is not a flash hotel type, no guests are
 * genuinely free, or no children/infants are in the search filters.
 */
export function getFlashFreeChildCount({
  offerType,
  filters,
  capacitiesByRate,
  includedGuestsByRate,
}: {
  offerType: App.OfferType
  filters?: App.OfferListFilters
  capacitiesByRate: Array<Array<App.RoomCapacity>>
  includedGuestsByRate?: Array<Array<App.PackageRoomIncludedGuests>>
}): number {
  if (!isFlashHotelType(offerType)) return 0

  const freeGuestsByRate = capacitiesByRate.map((capacities, i) => {
    const includedResult = getIncludedGuestTypesAtMaxAdults(includedGuestsByRate?.[i] ?? [])

    if (includedResult) {
      // includedGuests is the source of truth for pricing.
      // Cap by the maximum children/infants the room can physically hold (any entry).
      const maxPossibleChildren = capacities.length > 0 ? Math.max(...capacities.map(c => c.children)) : 0
      const maxPossibleInfants = capacities.length > 0 ? Math.max(...capacities.map(c => c.infants)) : 0

      return {
        freeChildren: Math.min(maxPossibleChildren, includedResult.maxIncludedChildren),
        freeInfants: Math.min(maxPossibleInfants, includedResult.maxIncludedInfants),
      }
    }

    // No includedGuests data — fall back to capacity heuristic
    const capacityResult = getFreeGuestTypesAtMaxOccupancy(capacities)
    return {
      freeChildren: capacityResult.maxFreeChildren,
      freeInfants: capacityResult.maxFreeInfants,
    }
  })

  // Best rate across all packages determines free guests per room
  const maxFreeChildrenPerRoom = Math.max(0, ...freeGuestsByRate.map(r => r.freeChildren))
  const maxFreeInfantsPerRoom = Math.max(0, ...freeGuestsByRate.map(r => r.freeInfants))

  if (maxFreeChildrenPerRoom === 0 && maxFreeInfantsPerRoom === 0) return 0

  // Sum up free guests across search rooms, capped by the best rate
  const rooms = filters?.rooms ?? []
  return sum(rooms, room =>
    Math.min(room.children ?? 0, maxFreeChildrenPerRoom) +
    Math.min(room.infants ?? 0, maxFreeInfantsPerRoom),
  )
}
